import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Skeleton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect } from "react";
import { useAppContext } from "../../context/AppContext";
import { DeactivatedUser } from "../../types";
import ApiService from "../../services/api";
import Layout from "../Layout";

function formatDate(isoString: string | Date | undefined): string {
  if (!isoString) {
    return "—";
  }

  try {
    const date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "—";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "—";
  }
}

export default function DistributorDeactivatedUser() {
  const {
    token,
    sessionId,
    getTranslation,
    getSkinTypeTranslation,
    getGenderTranslation,
    setSnackbar,
  } = useAppContext();

  const [deactivatedUsers, setDeactivatedUsers] = useState<DeactivatedUser[]>([]);
  const [filteredData, setFilteredData] = useState<DeactivatedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentPage = 1;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [timeFilter, setTimeFilter] = useState<string>('all');
  const [activatingUserId, setActivatingUserId] = useState<string | null>(null);

  // Media query for mobile view
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const getSearchPlaceholder = () => {
    const fullText = getTranslation("Txt886", "Search by name, age, gender, skintype or date");
    return fullText.split(' ')[0] || "Search";
  };

  // Fetch deactivated users
  useEffect(() => {
    const fetchDeactivatedUsers = async () => {
      if (!token) return;

      setIsLoading(true);

      try {
        const apiService = new ApiService(sessionId, token);
        const response = await apiService.getDeactivatedUsers(currentPage);
        
        if (response.data?.result) {
          setDeactivatedUsers(response.data.result);
        }
      } catch (err) {
        console.error('Error fetching deactivated users:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeactivatedUsers();
  }, [token, sessionId]);

  // Filter data based on search term and time filter
  useEffect(() => {
    let filtered = deactivatedUsers;

    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      if (timeFilter === '6months') {
        filterDate.setMonth(now.getMonth() - 6);
      } else if (timeFilter === '1year') {
        filterDate.setFullYear(now.getFullYear() - 1);
      }
      
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= filterDate;
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      
      filtered = filtered.filter((item) => {
        // Search in name
        const fullName = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
        if (fullName.includes(searchLower)) return true;
        
        // Search in email
        if (item.email?.toLowerCase().includes(searchLower)) return true;
        
        // Search in gender
        if (item.gender?.toLowerCase().includes(searchLower)) return true;
        
        // Search in age range
        if (item.age_range?.toLowerCase().includes(searchLower)) return true;
        
        // Search in skin type
        if (item.skin_type?.toLowerCase().includes(searchLower)) return true;
        
        // Search in phone number
        if (item.phone_number?.toLowerCase().includes(searchLower)) return true;
        
        // Search in _id
        if (item._id?.toLowerCase().includes(searchLower)) return true;
        
        return false;
      });
    }
    
    setFilteredData(filtered);
  }, [searchTerm, timeFilter, deactivatedUsers]);

  const handleActivateUser = async (userId: string) => {
    if (!token) {
      return;
    }

    setActivatingUserId(userId);
    try {
      const apiService = new ApiService(sessionId, token);
      const response = await apiService.activateUser(userId);
      
      if (response.success) {
        // Remove the activated user from the list
        setDeactivatedUsers(prev => prev.filter(user => user._id !== userId));
        setSnackbar({
          open: true,
          message: getTranslation("Txt985"),
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: getTranslation("Txt984"),
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('Error activating user:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to activate user',
        severity: 'error',
      });
    } finally {
      setActivatingUserId(null);
    }
  };

  return (
    <Layout showBackButton={true}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: "20px", md: "30px" },
          alignItems: { xs: "center", md: "center" },
          justifyContent: { xs: "flex-start", md: "flex-start" },
          padding: { xs: "0px", md: "20px" },
          width: "100%",
          height: "100%",
        }}
      >
        {/* Title */}
        <Typography 
          sx={{ 
            color: "var(--color-text-primary)",
            textAlign: "center",
            fontFamily: "HerbalifeFont",
            fontSize: { xs: "20px", md: "30px" },
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: { xs: "24px", md: "36px" },
          }}
        >
          {getTranslation("Txt981")}
        </Typography>

        {/* Search Bar and Filter */}
        <Box sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: "80%" },
          mb: { xs: 2, md: 0 },
          px: { xs: 1, md: 0 },
          display: "flex",
          gap: { xs: 1, sm: 2, md: 3 },
          flexDirection: { xs: "row", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          flexWrap: { xs: "nowrap", sm: "nowrap" }
        }}>
          <TextField
            variant="outlined"
            placeholder={getSearchPlaceholder()}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            dir="ltr"
            sx={{
              flex: { xs: 2, sm: 3, md: 4 },
              minWidth: 0,
              "& .MuiInputBase-input": {
                padding: { xs: "10px 12px", md: "12px 16px" },
                fontSize: { xs: "14px", md: "16px" },
                direction: "ltr",
                textAlign: "left",
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--color-text-secondary)", fontSize: { xs: "18px", sm: "20px", md: "24px" } }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "25px",
                backgroundColor: "var(--color-background)",
                boxShadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.10)",
                direction: "ltr",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-border-light)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-border-dark)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-border-dark)",
                },
              }
            }}
          />
          
          <FormControl 
            sx={{ 
              flex: { xs: 1, sm: 1, md: 1 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "25px",
                backgroundColor: "var(--color-background)",
                boxShadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.10)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-border-light)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-border-dark)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-border-dark)",
                },
              }
            }}
          >
            <Select
              value={timeFilter}
              onChange={(e: SelectChangeEvent) => setTimeFilter(e.target.value)}
              displayEmpty
              fullWidth
              MenuProps={{
                PaperProps: {
                    sx: {
                        "& .MuiMenu-list": {
                            padding: "8px 0 !important",
                            paddingLeft: "0 !important",
                        },
                        "& .MuiMenuItem-root": {
                            paddingLeft: "10px !important",
                        },
                    },
                },
            }}
              sx={{
                "& .MuiSelect-select": {
                  padding: { xs: "10px 12px", sm: "12px 14px", md: "12px 16px" },
                  fontSize: { xs: "13px", sm: "14px", md: "16px" },
                  minHeight: { xs: "40px", sm: "44px", md: "48px" },
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiSelect-icon": {
                  fontSize: { xs: "18px", sm: "20px", md: "24px" },
                  color: "var(--color-text-secondary)",
                }
              }}
            >
              <MenuItem value="all" sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}>
                {getTranslation("Txt323")}
              </MenuItem>
              <MenuItem value="6months" sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}>
                {getTranslation("Txt307")}
              </MenuItem>
              <MenuItem value="1year" sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}>
                {getTranslation("Txt889")}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table Container */}
        <Box
          sx={{
            width: { xs: "100%", md: "90%" },
            padding: 0,
            height: { xs: "100%", md: "auto" },
            flex: { xs: 1, md: "none" },
            display: { xs: "flex", md: "block" },
            flexDirection: { xs: "column", md: "row" },
            minHeight: 0,
          }}
        >
      
      {isXs ? (
        // Mobile design - Card layout
        <Box sx={{ 
          width: "100%", 
          display: "flex", 
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            {isLoading ? (
              // Skeleton loaders for mobile view
              Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: "16px 5px",
                    backgroundColor: index % 2 === 0 ? "var(--color-background)" : "#EFEFEF",
                    borderBottom: "2px solid #000",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: "8px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="40%" height={16} />
                      <Skeleton variant="text" width="60%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="50%" height={16} />
                      <Skeleton variant="text" width="70%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="45%" height={16} />
                      <Skeleton variant="text" width="55%" height={14} />
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="50%" height={16} />
                      <Skeleton variant="text" width="60%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                      <Skeleton variant="text" width="45%" height={16} />
                      <Skeleton variant="text" width="65%" height={14} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "flex-start" }}>
                      <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: "4px" }} />
                    </Box>
                  </Box>
                </Box>
              ))
            ) : filteredData.length === 0 ? (
              <Box 
                sx={{ 
                  p: "16px 5px",
                  backgroundColor: "var(--color-background)",
                  borderBottom: "2px solid #000",
                  textAlign: "center", 
                  py: 4, 
                  color: "var(--color-text-secondary)", 
                  flex: 1, 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  flexDirection: "column",
                  fontFamily: "HerbalifeFont"
                }}
              >
                <Typography 
                  sx={{ 
                    fontSize: { xs: "14px", md: "16px" },
                    fontWeight: 400,
                    color: "var(--color-text-secondary)",
                    fontFamily: "HerbalifeFont"
                  }}
                >
                  {getTranslation("Txt982")}
                </Typography>
              </Box>
            ) : (
              filteredData.map((row, index) => {
                const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
                const displayName = fullName || "User";

                return (
                  <Box
                    key={index}
                    sx={{
                      p: "16px 5px",
                      backgroundColor: index % 2 === 0 ? "var(--color-background)" : "#EFEFEF",
                      borderBottom: "2px solid #000",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    {/* Row 1: Date, Name, Gender */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: "8px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: "14px" }}>{getTranslation("Txt277")} </span>
                        <span style={{ fontWeight: 400, fontSize: "13px" }}>{formatDate(row.createdAt)}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt279")} </span>
                        <span style={{ fontWeight: 400 }}>{displayName}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt883")}: </span>
                        <span style={{ fontWeight: 400 }}>{row.gender ? getGenderTranslation(row.gender) : "—"}</span>
                      </Box>
                    </Box>

                    {/* Row 2: Age Group, Skin Type, Account Status */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt884")}: </span>
                        <span style={{ fontWeight: 400, fontSize: "13px" }}>{row.age_range || "—"}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt885")}: </span>
                        <span style={{ fontWeight: 400 }}>{row.skin_type ? getSkinTypeTranslation(row.skin_type) : "—"}</span>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", fontSize: "14px", color: "var(--color-text-primary)", textAlign: "left", flex: 1 }}>
                        <span style={{ fontWeight: 600 }}>{getTranslation("Txt988")}: </span>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={activatingUserId === row._id}
                          onClick={() => handleActivateUser(row._id)}
                          sx={{
                            backgroundColor: "#4caf50",
                            color: "var(--color-text-inverse)",
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            minWidth: "auto",
                            "&:hover": {
                              backgroundColor: "#388e3c",
                            },
                            "&:disabled": {
                              backgroundColor: "#a5d6a7",
                              color: "var(--color-text-inverse)",
                            },
                          }}
                        >
                          {activatingUserId === row._id ? "Activating..." : "Activate"}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "10px",
            boxShadow: 0,
            backgroundColor: "var(--color-background)",
            minHeight: { xs: "100%", md: "300px" },
            maxHeight: { xs: "100%", md: "300px" },
            height: { xs: "100%", md: "auto" },
            overflow: "visible",
            position: "relative",
            display: { xs: "flex", md: "block" },
            flexDirection: "column",
            flex: 1,
            overflowY: "auto",
          }}
        >
          <Table
            sx={{
              minWidth: { xs: 800, md: "auto" },
              backgroundColor: "var(--color-background)",
              height: { xs: "100%", md: "auto" },
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 9,
              }}
            >
              <TableRow sx={{ backgroundColor: "#DBDBDA" }}>
                {[
                  getTranslation("Txt615"), // Date
                  getTranslation("Txt939"), // Name
                  getTranslation("Txt883"), // Gender
                  getTranslation("Txt884"), // Age Group
                  getTranslation("Txt885"), // Skin Type
                  getTranslation("Txt988"), // Action
                ].map((header) => (
                  <TableCell
                    key={String(header)}
                    sx={{
                      ...headerStyle,
                      display: "table-cell",
                      width: "16.66%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                // Skeleton loaders for desktop table view
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "var(--color-background)" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="70%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="50%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="40%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="text" width="60%" height={20} />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, width: "16.66%" }}>
                      <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: "4px" }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell 
                    colSpan={6} 
                    sx={{ 
                      textAlign: "center", 
                      py: 4, 
                      color: "var(--color-text-secondary)",
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 400,
                      fontFamily: "HerbalifeFont"
                    }}
                  >
                  {getTranslation("Txt982")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row, index) => {
                  const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
                  const displayName = fullName || "User";

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "var(--color-background)" },
                        borderBottom: "none",
                      }}
                    >
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={formatDate(row.createdAt)}
                      >
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "20.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                        title={displayName}
                      >
                        {displayName}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.gender ? getGenderTranslation(row.gender) : "—"}
                      >
                        {row.gender ? getGenderTranslation(row.gender) : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.age_range || "—"}
                      >
                        {row.age_range || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.skin_type ? getSkinTypeTranslation(row.skin_type) : "—"}
                      >
                        {row.skin_type ? getSkinTypeTranslation(row.skin_type) : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellStyle,
                          display: "table-cell",
                          width: "16.66%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          disabled={activatingUserId === row._id}
                          onClick={() => handleActivateUser(row._id)}
                          sx={{
                            backgroundColor: "#4caf50",
                            color: "var(--color-text-inverse)",
                            padding: "10px 16px 6px 16px",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            minWidth: "auto",
                            "&:hover": {
                              backgroundColor: "#388e3c",
                            },
                            "&:disabled": {
                              backgroundColor: "#a5d6a7",
                              color: "var(--color-text-inverse)",
                            },
                          }}
                        >
                         {getTranslation("Txt983")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
        </Box>
      </Box>
    </Layout>
  );
}

const headerStyle = {
  fontWeight: 700,
  fontSize: { xs: "10px", md: "14px" },
  textAlign: "left",
  color: "var(--color-text-primary)",
  backgroundColor: "#DBDBDA",
  lineHeight: { xs: "16px", md: "20px" },
  padding: { xs: "12px 8px 6px 8px", md: "20px 20px 8px 20px" },
  whiteSpace: "nowrap",
  width: { xs: "60px", md: "auto" },
};

const cellStyle = {
  fontSize: { xs: "11px", md: "16px" },
  color: "var(--color-text-primary)",
  fontWeight: 400,
  textAlign: "left",
  margin: "auto",
  lineHeight: { xs: "16px", md: "20px" },
  padding: { xs: "8px 6px 8px 6px", md: "10px 20px 10px 20px" },
  borderBottom: "none",
  whiteSpace: "nowrap",
  width: { xs: "60px", md: "auto" },
};
